<script setup>
// GK-HUB: Reusable central modal showing a conversation's messages (read-only).
// Used by CSAT and Recorrência drill-downs. Fetches via existing
// ispReports/fetchConversasDetail store action.
import { computed, watch, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  conversationId: { type: [Number, String], default: null },
});
const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const detailState = computed(
  () => store.getters['ispReports/getConversasDetail'] || {}
);
const isFetching = computed(() => detailState.value.isFetching || false);
const detail = computed(() => detailState.value.data || null);
const conversation = computed(() => detail.value?.conversation || null);
const messages = computed(() => detail.value?.messages || []);

const currentAccountId = computed(() => store.getters.getCurrentAccountId);
const fullConversationUrl = computed(() => {
  if (!conversation.value?.display_id) return '#';
  return `/app/accounts/${currentAccountId.value}/conversations/${conversation.value.display_id}`;
});

// Fetch when conversationId changes
watch(
  () => props.conversationId,
  newId => {
    if (newId) {
      store.dispatch('ispReports/fetchConversasDetail', newId);
    }
  },
  { immediate: true }
);

// ESC to close
const onKeydown = e => {
  if (e.key === 'Escape') emit('close');
};
watch(
  () => props.conversationId,
  val => {
    document.removeEventListener('keydown', onKeydown);
    if (val) document.addEventListener('keydown', onKeydown);
  }
);
onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown);
});

// ── UI helpers ──
const formatDate = iso => {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const formatTimeOnly = iso => {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const statusBadge = status => {
  const map = {
    open: {
      label: t('ISP_REPORTS.CONVERSAS.STATUS_OPEN'),
      class: 'bg-green-500/10 text-green-600',
    },
    resolved: {
      label: t('ISP_REPORTS.CONVERSAS.STATUS_RESOLVED'),
      class: 'bg-n-blue-9/10 text-n-blue-11',
    },
    pending: {
      label: t('ISP_REPORTS.CONVERSAS.STATUS_PENDING'),
      class: 'bg-amber-500/10 text-amber-600',
    },
    snoozed: {
      label: t('ISP_REPORTS.CONVERSAS.STATUS_SNOOZED'),
      class: 'bg-n-slate-9/10 text-n-slate-11',
    },
  };
  return (
    map[status] || { label: status, class: 'bg-n-solid-3 text-n-slate-10' }
  );
};

// Bubble styles: outgoing = brand-blue solid, incoming = slate card
const bubbleStyle = msg => {
  if (msg.is_private) return 'bg-amber-500/10 border border-amber-500/30';
  if (msg.message_type === 'incoming') {
    return 'bg-n-solid-3 border border-n-weak';
  }
  return 'bg-n-blue-9/10 border border-n-blue-9/30';
};

const msgSenderClass = msg => {
  if (msg.is_private) return 'text-amber-600';
  return msg.message_type === 'incoming' ? 'text-n-slate-11' : 'text-n-blue-11';
};

const msgIcon = msg => {
  if (msg.is_private) return 'i-lucide-lock';
  return msg.message_type === 'incoming' ? 'i-lucide-user' : 'i-lucide-headset';
};

// GK-HUB: CSAT callout styling — background/avatar tinted by rating tier
const csatCalloutBg = computed(() => {
  const r = conversation.value?.csat_rating;
  if (!r) return '';
  if (r >= 5) return 'bg-green-500/8';
  if (r >= 4) return 'bg-n-blue-9/8';
  if (r >= 3) return 'bg-amber-500/8';
  return 'bg-red-500/8';
});
const csatAvatarBg = computed(() => {
  const r = conversation.value?.csat_rating;
  if (!r) return '';
  if (r >= 5) return 'bg-green-500';
  if (r >= 4) return 'bg-n-blue-9';
  if (r >= 3) return 'bg-amber-500';
  return 'bg-red-500';
});
const csatLabelColor = computed(() => {
  const r = conversation.value?.csat_rating;
  if (!r) return '';
  if (r >= 5) return 'text-green-600';
  if (r >= 4) return 'text-n-blue-11';
  if (r >= 3) return 'text-amber-600';
  return 'text-red-500';
});

// Inline stars (small) for the header row
const renderStarsInline = rating =>
  '\u2605'.repeat(rating) + '\u2606'.repeat(5 - rating);

// GK-HUB: Parse CSAT message options and mark which one the customer selected.
// content_attributes.items = [{value: 'csat_5', title: '🤩 Ótimo'}, ...]
// content_attributes.submitted_values.csat_survey_response.rating = 5
const csatOptions = msg => {
  const attrs = msg.content_attributes || {};
  const items = attrs.items || [];
  const submittedRating = attrs.submitted_values?.csat_survey_response?.rating;
  return items.map(it => {
    // Extract numeric rating from "csat_5" → 5
    const match = String(it.value || '').match(/(\d+)/);
    const itemRating = match ? Number(match[1]) : null;
    return {
      value: it.value,
      title: it.title,
      selected: itemRating != null && itemRating === submittedRating,
    };
  });
};

const msgIsSubmitted = msg => {
  return !!msg.content_attributes?.submitted_values?.csat_survey_response
    ?.rating;
};
</script>

<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="conversationId"
        class="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
        @click.self="emit('close')"
      >
        <Transition name="modal-scale" appear>
          <div
            class="relative bg-n-solid-2 rounded-[12px] shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden border border-n-weak"
          >
            <!-- Loading state -->
            <div
              v-if="isFetching && !conversation"
              class="flex items-center justify-center h-64"
            >
              <woot-loading-state :message="t('ISP_REPORTS.LOADING')" />
            </div>

            <template v-if="conversation">
              <!-- Header -->
              <div
                class="sticky top-0 bg-n-solid-2 z-10 border-b border-n-weak px-5 py-4 flex-shrink-0"
              >
                <div class="flex items-start justify-between gap-4">
                  <div class="min-w-0 flex-1">
                    <div class="flex items-center gap-2 mb-1">
                      <span
                        class="text-n-blue-11 font-mono text-xs tabular-nums"
                      >
                        <!-- eslint-disable vue/no-bare-strings-in-template -->
                        #GKC-{{ conversation.display_id }}
                        <!-- eslint-enable vue/no-bare-strings-in-template -->
                      </span>
                      <span
                        class="inline-block text-[10px] font-medium px-2 py-0.5 rounded-full"
                        :class="statusBadge(conversation.status).class"
                      >
                        {{ statusBadge(conversation.status).label }}
                      </span>
                    </div>
                    <h2
                      class="text-base font-semibold text-n-slate-12 truncate"
                    >
                      {{ conversation.contact_name || '-' }}
                    </h2>
                    <div
                      class="flex items-center gap-2 text-[11px] text-n-slate-9 mt-1 flex-wrap"
                    >
                      <span
                        v-if="conversation.agent_name"
                        class="inline-flex items-center gap-1"
                      >
                        <span class="i-lucide-headset w-3 h-3 inline-block" />
                        {{ conversation.agent_name }}
                        <!-- GK-HUB: IA badge in modal header -->
                        <span
                          v-if="conversation.is_ia_agent"
                          class="gk-ia-badge"
                        >
                          {{ $t('CONVERSATION.IA_BADGE') }}
                        </span>
                      </span>
                      <span v-if="conversation.inbox_name">
                        · {{ conversation.inbox_name }}
                      </span>
                      <span>· {{ formatDate(conversation.created_at) }}</span>
                      <!-- eslint-disable vue/no-bare-strings-in-template -->
                      <span v-if="conversation.csat_rating">
                        · {{ conversation.csat_rating }}/5 ★
                      </span>
                      <!-- eslint-enable vue/no-bare-strings-in-template -->
                    </div>
                  </div>
                  <button
                    class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-n-solid-3 transition-colors cursor-pointer flex-shrink-0"
                    :aria-label="t('ISP_REPORTS.CLOSE')"
                    @click="emit('close')"
                  >
                    <span class="i-lucide-x w-4 h-4 text-n-slate-10" />
                  </button>
                </div>
              </div>

              <!-- GK-HUB: CSAT response callout (prominent, highlights the customer rating) -->
              <div
                v-if="conversation.csat_rating"
                class="px-5 py-4 border-b border-n-weak flex-shrink-0"
                :class="csatCalloutBg"
              >
                <div class="flex items-start gap-3">
                  <div
                    class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white text-lg font-bold"
                    :class="csatAvatarBg"
                  >
                    {{ conversation.csat_rating }}
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 mb-1">
                      <span
                        class="text-[11px] uppercase tracking-wider font-semibold text-n-slate-9"
                      >
                        {{ t('ISP_REPORTS.CSAT.DETAIL_CALLOUT_LABEL') }}
                      </span>
                      <span
                        class="csat-stars text-[14px] leading-none"
                        :class="`csat-stars--${conversation.csat_rating}`"
                      >
                        {{ renderStarsInline(conversation.csat_rating) }}
                      </span>
                      <span
                        class="text-[11px] font-medium"
                        :class="csatLabelColor"
                      >
                        {{
                          t(
                            `ISP_REPORTS.CSAT.RATING_${conversation.csat_rating}`
                          )
                        }}
                      </span>
                    </div>
                    <!-- eslint-disable vue/no-bare-strings-in-template -->
                    <p
                      v-if="conversation.csat_feedback"
                      class="text-[13px] text-n-slate-12 leading-relaxed"
                    >
                      "{{ conversation.csat_feedback }}"
                    </p>
                    <!-- eslint-enable vue/no-bare-strings-in-template -->
                    <p v-else class="text-[12px] italic text-n-slate-9">
                      {{ t('ISP_REPORTS.CSAT.DRILL_NO_COMMENT') }}
                    </p>
                    <div
                      v-if="conversation.csat_answered_at"
                      class="text-[10px] text-n-slate-9 mt-1.5"
                    >
                      {{ formatDate(conversation.csat_answered_at) }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Body: messages -->
              <div class="flex-1 overflow-y-auto p-5">
                <div
                  v-if="!messages.length"
                  class="text-center text-sm text-n-slate-9 py-12"
                >
                  {{ t('ISP_REPORTS.CONVERSAS.DETAIL_NO_MESSAGES') }}
                </div>
                <div v-else class="space-y-3">
                  <div
                    v-for="msg in messages"
                    :key="msg.id"
                    :class="
                      msg.message_type === 'incoming' ? '' : 'flex justify-end'
                    "
                  >
                    <!-- GK-HUB: Special rendering for input_csat messages —
                         shows the question + all rating options, highlighting
                         the one the customer picked. content_type is the
                         discriminator (message_type is just outgoing/incoming). -->
                    <div
                      v-if="msg.content_type === 'input_csat'"
                      class="w-full max-w-[85%] rounded-xl p-3 bg-purple-500/8 border border-purple-500/20"
                    >
                      <div
                        class="flex items-center gap-1.5 mb-2 text-[10px] text-purple-600 font-semibold uppercase tracking-wider"
                      >
                        <span class="i-lucide-star w-3 h-3" />
                        {{ t('ISP_REPORTS.CSAT.SURVEY_QUESTION_LABEL') }}
                      </div>
                      <div
                        v-if="msg.content"
                        class="text-[13px] text-n-slate-12 font-medium mb-3 whitespace-pre-wrap"
                      >
                        {{ msg.content }}
                      </div>
                      <div class="flex flex-wrap gap-2">
                        <span
                          v-for="opt in csatOptions(msg)"
                          :key="opt.value"
                          class="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] font-medium border transition-colors"
                          :class="
                            opt.selected
                              ? 'bg-green-500/15 border-green-500/40 text-green-600'
                              : 'bg-n-solid-3 border-n-weak text-n-slate-10'
                          "
                        >
                          {{ opt.title }}
                          <span
                            v-if="opt.selected"
                            class="i-lucide-check w-3 h-3"
                          />
                        </span>
                      </div>
                      <div
                        v-if="msgIsSubmitted(msg)"
                        class="text-[10px] text-n-slate-9 mt-2"
                      >
                        {{ formatTimeOnly(msg.created_at) }} ·
                        {{ t('ISP_REPORTS.CSAT.SURVEY_ANSWERED') }}
                      </div>
                    </div>

                    <div
                      v-else
                      class="max-w-[80%] rounded-xl p-3 text-[13px]"
                      :class="bubbleStyle(msg)"
                    >
                      <div class="flex items-center gap-1.5 mb-1 text-[10px]">
                        <span :class="msgIcon(msg)" class="w-3 h-3" />
                        <span class="font-medium" :class="msgSenderClass(msg)">
                          {{ msg.sender_name }}
                        </span>
                      </div>
                      <!-- Attachments -->
                      <template
                        v-if="msg.attachments && msg.attachments.length"
                      >
                        <div
                          v-for="att in msg.attachments"
                          :key="att.id"
                          class="mb-1.5"
                        >
                          <a
                            v-if="att.file_type === 'image'"
                            :href="att.data_url"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <img
                              :src="att.thumb_url || att.data_url"
                              class="rounded-lg max-w-full max-h-48 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                              loading="lazy"
                            />
                          </a>
                          <audio
                            v-else-if="att.file_type === 'audio'"
                            :src="att.data_url"
                            controls
                            preload="metadata"
                            class="w-full max-w-[240px] h-8"
                          />
                          <video
                            v-else-if="att.file_type === 'video'"
                            :src="att.data_url"
                            controls
                            preload="metadata"
                            class="rounded-lg max-w-full max-h-48"
                          />
                          <a
                            v-else
                            :href="att.data_url"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="flex items-center gap-2 px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak hover:bg-n-alpha-2 transition-colors"
                          >
                            <span
                              class="i-lucide-file-down w-4 h-4 text-n-slate-9 flex-shrink-0"
                            />
                            <span
                              class="text-xs text-n-slate-11 truncate max-w-[180px]"
                            >
                              {{
                                att.extension
                                  ? `arquivo.${att.extension}`
                                  : t('ISP_REPORTS.CONVERSAS.DETAIL_DOWNLOAD')
                              }}
                            </span>
                          </a>
                        </div>
                      </template>
                      <div
                        v-if="msg.content"
                        class="whitespace-pre-wrap break-words leading-relaxed text-n-slate-11"
                      >
                        {{ msg.content }}
                      </div>
                      <div class="text-[10px] opacity-60 mt-1 text-right">
                        {{ formatTimeOnly(msg.created_at) }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Footer: open full conversation link -->
              <div
                class="flex items-center justify-end gap-3 px-5 py-3 border-t border-n-weak bg-n-solid-3 flex-shrink-0"
              >
                <!-- GK-HUB: botão secundário com border — antes era só texto (parecia link) -->
                <button
                  class="inline-flex items-center gap-1.5 bg-n-solid-2 border border-n-weak text-n-slate-12 text-sm font-medium px-4 py-1.5 rounded-md hover:bg-n-alpha-1 hover:border-n-slate-6 transition-colors cursor-pointer"
                  @click="emit('close')"
                >
                  {{ t('ISP_REPORTS.CLOSE') }}
                </button>
                <!-- GK-HUB: target="_blank" abre a conversa completa em nova aba -->
                <a
                  :href="fullConversationUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center gap-1.5 bg-n-brand text-white text-sm font-medium px-4 py-1.5 rounded-md hover:opacity-90 transition-opacity"
                >
                  {{ t('ISP_REPORTS.OPEN_FULL_CONVERSATION') }}
                  <span class="i-lucide-arrow-right w-3.5 h-3.5" />
                </a>
              </div>
            </template>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.18s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.modal-scale-enter-active,
.modal-scale-leave-active {
  transition: all 0.22s cubic-bezier(0.16, 1, 0.3, 1);
}
.modal-scale-enter-from,
.modal-scale-leave-to {
  opacity: 0;
  transform: scale(0.96) translateY(8px);
}

/* GK-HUB: CSAT stars colors (same tiers as IspCsat) */
.csat-stars {
  letter-spacing: 1px;
}
.csat-stars--5 {
  color: #10b981;
}
.csat-stars--4 {
  color: #5dade2;
}
.csat-stars--3 {
  color: #f59e0b;
}
.csat-stars--2,
.csat-stars--1 {
  color: #ef4444;
}
</style>
